const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/PDFViewer/PDFDocumentController.tsx';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { Download, KeyboardArrowDown, KeyboardArrowUp, Undo, Visibility } from '@mui/icons-material';
import { css, cx } from '@emotion/css';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import textMap from './translations';
import { IconClass } from '../../defs/icons';
import { addDividers } from '../../utils/ui';
import { Knopf } from '../Knopf/Knopf';
import { usePDFViewer } from './PDFViewerProvider';

interface Props {
    className?: string;
    showSignatureControls?: boolean;
    showDownload?: boolean;
    showZoom?: boolean;
    showPaging?: boolean;
}

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            cssPDFDocumentController: css`
                display: flex;
                gap: ${theme.spacing(1)};
                align-items: center;
            `,
            divider: css`
                height: 20px;
                width: 1px;
                background-color: #e2e2e2;
            `,
        }),
        [theme]
    );
}

/**
 * Toolbar variant of the PDF document controller.
 *
 * Purely presentational: all state and actions come from the surrounding
 * PDFViewerProvider, so this component can live anywhere in the tree
 * (dialog title bar, app-bar footer, ...) and can mount/unmount freely
 * without affecting the document state.
 */
export const PDFDocumentController = (props: Props) => {
    const {
        className,
        showSignatureControls = false,
        showDownload = true,
        showZoom = true,
        showPaging = false,
    } = props;

    const theme = useTheme();
    const isSmallDevice = useMediaQuery(theme.breakpoints.down('sm'));
    const classes = useCSSClasses();
    const ctl = usePDFViewer();

    const renderPageNav = () => (
        <React.Fragment key="pageNav">
            <Knopf
                actionType="neutral"
                disabled={ctl.disabled || ctl.drawingEnabled || ctl.pageNumber <= 1}
                do={ctl.showPreviousPage}
                hideTextWhenNarrow={true}
                icon={<KeyboardArrowUp />}
                type="text"
            />
            <Knopf
                actionType="neutral"
                disabled={ctl.disabled || ctl.drawingEnabled || ctl.pageNumber >= ctl.pageCount}
                do={ctl.showNextPage}
                hideTextWhenNarrow={true}
                icon={<KeyboardArrowDown />}
                type="text"
            />
        </React.Fragment>
    );

    const renderZoomControls = () => (
        <React.Fragment key="zoomControls">
            <Knopf
                actionType="neutral"
                disabled={ctl.disabled || !ctl.canZoomOut}
                do={ctl.zoomOut}
                hideTextWhenNarrow={true}
                icon={IconClass.Minus}
                type="text"
            />
            <Knopf
                actionType="neutral"
                disabled={ctl.disabled || !ctl.canZoomIn}
                do={ctl.zoomIn}
                hideTextWhenNarrow={true}
                icon={IconClass.Plus}
                type="text"
            />
        </React.Fragment>
    );

    const renderDownloadButton = () => (
        <Knopf
            key="downloadButton"
            actionType="neutral"
            disabled={ctl.disabled}
            do={ctl.startDownload}
            hideTextWhenNarrow={true}
            icon={<Download />}
            type="text"
        />
    );

    const renderUndoButton = () => (
        <Knopf
            key="undoButton"
            actionType="neutral"
            disabled={ctl.disabled || !ctl.hasActiveDrawing}
            do={ctl.undo}
            hideTextWhenNarrow={true}
            icon={<Undo />}
            type="text"
        />
    );

    const renderSaveButton = () => (
        <Knopf
            key="saveButton"
            disabled={ctl.isSaving || ctl.disabled || !ctl.canSave}
            do={ctl.save}
            hideTextWhenNarrow={true}
            icon={IconClass.Save}
            text={textMap.saveSignature}
        />
    );

    const renderSignatureButton = () => {
        const icon = ctl.drawingEnabled ? <Visibility /> : IconClass.Pencil;
        return (
            <Knopf
                key="signatureButton"
                disabled={ctl.disabled}
                do={ctl.toggleDocumentEditMode}
                hideTextWhenNarrow={true}
                icon={icon}
            />
        );
    };

    const renderSignatureControls = () => (
        <React.Fragment key="signatureControls">
            {ctl.drawingEnabled ? renderUndoButton() : renderSaveButton()}
            {renderSignatureButton()}
        </React.Fragment>
    );

    const children = [
        showPaging ? renderPageNav() : null,
        showZoom ? renderZoomControls() : null,
        showDownload ? renderDownloadButton() : null,
        showSignatureControls ? renderSignatureControls() : null,
    ];

    return (
        <div className={cx('PDFDocumentController', classes.cssPDFDocumentController, className)}>
            {isSmallDevice ? children : addDividers(_.compact(children), classes.divider)}
        </div>
    );
};
